import * as React from 'react';
import { RichTextEditor } from '@web-config-app/core-react-ui';
import { Box } from '@leagueplatform/genesis-core';
import { useConfigApp, useLocales } from '@web-config-app/core-react';
import type { ControlProps } from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';
import { AllowedUriProtocols } from '@web-config-app/core';
import { useRichTextInputControlCacheKey } from './use-rich-text-input-control-cache-key';

export const RichTextInputControlContents: React.FC<
  ControlProps & {
    cacheKey: string | undefined;
    currentLocale: string;
    hasMultipleLocales?: boolean;
    allowedUriProtocols: AllowedUriProtocols;
  }
> = ({
  cacheKey,
  currentLocale,
  hasMultipleLocales,
  allowedUriProtocols,
  ...props
}) => {
  const [minHeight, setMinHeight] = React.useState<number>(100);
  const richTextEditorElem = React.useRef<HTMLDivElement | null>(null);

  // using minHeight on a placeholder div reduces the amount of layout thrashing
  // when doing operations that re-create the entire rich text editor (ex: re-ordering
  // an array of rich text controls)

  React.useEffect(() => {
    setMinHeight(
      Math.max(minHeight, richTextEditorElem.current?.clientHeight ?? 0),
    );
  }, [minHeight]);

  return (
    <EntityFormControl
      {...props}
      renderControl={({
        data,
        id,
        handleChange,
        path,
        inputStatus,
        enabled,
        localized,
      }) => {
        const handleOnChange = (value: string) => {
          // when a user deletes all the content in the editor, tiptap richtext retains a single <p></p> tag node, so we clear it out
          if (value === '<p></p>') {
            // handle change with an empty string to maintain array consistency for localization
            handleChange(path, '');
            return;
          }
          handleChange(path, value);
        };

        return cacheKey && cacheKey.length > 0 ? (
          <Box ref={richTextEditorElem}>
            <RichTextEditor
              id={id}
              value={data ?? ''}
              editorCacheKey={cacheKey}
              onChange={handleOnChange}
              readOnly={!enabled}
              inputStatus={inputStatus}
              currentLocale={currentLocale}
              hasMultipleLocales={localized ? hasMultipleLocales : false}
              allowedUriProtocols={allowedUriProtocols}
            />
          </Box>
        ) : (
          <Box
            css={{
              minHeight,
              width: '100%',
              borderRadius: '$medium',
              border: '$borderWidths$thin solid $interactiveBorderDefault',
            }}
          />
        );
      }}
    />
  );
};

export const RichTextInputControl: React.FC<ControlProps> = (props) => {
  const { data: value } = props;
  const { currentLocale, hasMultipleLocales } = useLocales();
  const { frontendValidation } = useConfigApp();

  const cacheKey = useRichTextInputControlCacheKey(currentLocale, value);

  return (
    <RichTextInputControlContents
      {...props}
      cacheKey={cacheKey}
      currentLocale={currentLocale}
      hasMultipleLocales={hasMultipleLocales}
      allowedUriProtocols={frontendValidation?.uriSchemePolicy?.allowed}
    />
  );
};
